import DesktopTower from '@mds/icons/DesktopTower';
import Moon from '@mds/icons/Moon';
import Sun from '@mds/icons/Sun';
import { themeVars } from '@mds/ui/theme/themeVars';
import { Chip, chipClasses, Divider, Stack, useColorScheme, type ChipProps, type Theme } from '@mui/material';
import React, { type ReactElement } from 'react';
import { AccountMenuItem, AccountMenuItemProps } from '../AccountMenuItem';

/**
 * Figma (Theme toggle, node 6490:40404) specifies an inverse-contrast selected fill
 * (`action.selectedContrast` + `text.contrast`, matching the same pair used by the selected
 * Checkbox fill and DatePicker day) — but that reads as too dark in light mode, so the
 * selected chip only uses it in dark mode. In light mode it falls back to the same
 * `--mds-Chip-filledBg`/`--mds-Chip-filledColor` tokens the generic Chip "filled" variant
 * already uses (a branded purple fill, same polarity as the light-mode page — no inversion
 * needed there). The unselected chip gets a subtle fill, `border.subtle` border, and
 * `text.primary`/`text.secondary` label/icon — not the generic Chip "soft" default-color
 * tokens, which have no border and don't invert with the color scheme.
 *
 * On hover (per the shared `<Chip>` component's own "hovered" state, node
 * 7581:1157/7586:6786), every variant layers a flat ink tint over the resting fill via
 * `backgroundImage` — the same overlay mechanic the theme already uses for hover. Which ink
 * to use depends on the fill's polarity relative to the page: the unselected chip and the
 * light-mode selected chip (branded purple, same polarity as the page) use `action.hover`
 * directly, matching Figma's generic reference exactly. The dark-mode selected chip (white,
 * intentionally inverted from the dark page) needs the *opposite* mode's `action.hover` ink
 * instead — overlaying the page's own hover ink on an already-inverted surface is the same
 * tone as the surface and reads as no visible change at all (confirmed by inspecting the
 * rendered chip). `action.hover`'s ink component is numerically identical to the *other*
 * mode's `text.contrast` value by design (light-mode `action.hover` = `#0e0d2414`, the same
 * `#0e0d24` as dark-mode `text.contrast`), so the literal below is that known pair, not an
 * arbitrary hardcoded color — `theme.alpha()` can't derive it at runtime instead, since MDS's
 * CSS-vars theme only emits a `*Channel` variable for the standard MUI palette shape, not for
 * MDS-only additions like `text.contrast`.
 *
 * Overrides the actual `backgroundColor`/`color`/`border` properties directly rather than
 * the Chip theme's own `--mds-Chip-*` custom properties: the theme sets those same custom
 * properties again inside a `:where([data-color-scheme="dark"])` block emitted as its own,
 * later stylesheet rule, which would otherwise win over a same-specificity override made
 * through the custom properties in dark mode. The theme's own "filled" (solid) variant also
 * nests its base colors under a literal `'&'` key rather than flat properties (unlike
 * "soft") — matching that exact key shape (here and for `'&:hover'`) is required, or the
 * override loses to the theme's block regardless of interpolation order. Dark-mode overrides
 * use `theme.applyStyles('dark', …)`, the same mode-branching helper the rest of this theme
 * uses for genuinely mode-specific values (see `commonChipColorVariables` in `chips.ts`).
 * Focus-visible is left untouched — the shared component's own ring border
 * (`action.focusOutline`, node 7581:989's `BorderFocused`) is already correct as-is.
 */
const LIGHT_MODE_ACTION_HOVER = '#0e0d2414';

const themeChipSx = (selected: boolean) => (theme: Theme) => {
  const palette = themeVars(theme).palette;
  const hoverOverlay = (tint: string) => `linear-gradient(${tint}, ${tint})`;
  const unselectedHoverOverlay = hoverOverlay(palette.action.hover);
  return selected
    ? {
        '&': {
          backgroundColor: 'var(--mds-Chip-filledBg)',
          color: 'var(--mds-Chip-filledColor)',
          ...theme.applyStyles('dark', {
            backgroundColor: palette.action.selectedContrast,
            color: palette.text.contrast,
          }),
        },
        '&:hover': {
          backgroundColor: 'var(--mds-Chip-filledBg)',
          color: 'var(--mds-Chip-filledColor)',
          backgroundImage: hoverOverlay(palette.action.hover),
          ...theme.applyStyles('dark', {
            backgroundColor: palette.action.selectedContrast,
            color: palette.text.contrast,
            backgroundImage: hoverOverlay(LIGHT_MODE_ACTION_HOVER),
          }),
        },
      }
    : {
        backgroundColor: palette.action.enabled,
        color: palette.text.primary,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: palette.border.subtle,
        [`& .${chipClasses.icon}`]: { color: palette.text.secondary },
        '&:hover': {
          backgroundColor: palette.action.enabled,
          color: palette.text.primary,
          borderColor: palette.border.subtle,
          backgroundImage: unselectedHoverOverlay,
        },
      };
};

const compactThemeChipRootStyle: React.CSSProperties = {
  justifyContent: 'center',
  minWidth: 32,
  padding: 0,
  width: 32,
};

const compactThemeChipIconStyle: React.CSSProperties = {
  margin: 0,
};

const compactThemeChipLabelStyle: React.CSSProperties = {
  display: 'none',
  padding: 0,
};

const themeModes = ['light', 'dark', 'system'] as const;
export type ThemeMode = (typeof themeModes)[number];

const themeModeIcons: Record<ThemeMode, ReactElement<{ style?: React.CSSProperties }>> = {
  light: <Sun />,
  dark: <Moon />,
  system: <DesktopTower />,
};

export interface ThemeToggleItemProps
  extends Omit<ChipProps, 'icon' | 'label' | 'role' | 'variant' | 'onClick' | 'sx' | 'slotProps'> {
  mode: ThemeMode;
  /** When true, renders an icon-only chip without its text label. */
  compact?: boolean;
  /** Defaults to whether `mode` matches the current color scheme. */
  selected?: boolean;
  /** Defaults to calling `useColorScheme().setMode(mode)`. */
  onClick?: () => void;
}

/**
 * A single theme option chip, e.g. the "Dark" chip inside `ThemeToggle`. Exported so a rail
 * nav (`SideNav`) can show just the currently active mode as the same chip look (bordered
 * circle in `compact` mode) instead of reimplementing it - see `SideNav`'s
 * `FullShell` Storybook story.
 *
 * Forwards every other prop (`onMouseEnter`, `onFocus`, `aria-expanded`, ...) straight to the
 * underlying `Chip` - required for it to work as a `SideNavFlyout` `trigger`, which clones
 * those handlers onto whatever element it's given; a component that silently drops unknown
 * props (as this one used to) ends up reacting to a click (an explicitly named prop here)
 * but not a hover (which isn't).
 *
 * When rendering several as a group (like `ThemeToggle` does), pass `selected`/`onClick`
 * explicitly from one shared `useColorScheme()` call in the parent instead of letting each
 * item read/write independently - reads from one item's hook call don't reliably reflect a
 * write made via a different item's hook call within the same render.
 */
export function ThemeToggleItem({
  mode,
  compact = false,
  selected: selectedProp,
  onClick: onClickProp,
  ...rest
}: ThemeToggleItemProps) {
  const { mode: currentMode, setMode } = useColorScheme();
  const selected = selectedProp ?? currentMode === mode;
  const icon = compact
    ? React.cloneElement(themeModeIcons[mode], { style: compactThemeChipIconStyle })
    : themeModeIcons[mode];

  return (
    <Chip
      {...rest}
      icon={icon}
      label={compact ? '' : upperFirst(mode)}
      role="button"
      aria-label={`Switch to ${mode} theme`}
      variant={selected ? 'solid' : 'soft'}
      aria-pressed={selected ? 'true' : 'false'}
      sx={themeChipSx(selected)}
      slotProps={
        compact
          ? {
              root: { style: compactThemeChipRootStyle },
              label: { style: compactThemeChipLabelStyle },
            }
          : undefined
      }
      onClick={onClickProp ?? (() => setMode(mode))}
    />
  );
}

export interface ThemeToggleProps extends AccountMenuItemProps {
  /** When true, renders icon-only chips without text labels. */
  compact?: boolean;
  /** Controls how theme options are arranged. */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Hide the trailing divider `ThemeToggle` renders by default. The default divider is
   * part of the standard account-menu panel layout (see the "Account / Sign-out Side
   * Panel" spec) - only set this when composing `ThemeToggle` outside that context, e.g.
   * as a standalone row where you're supplying your own section divider instead.
   */
  disableDivider?: boolean;
}

export function ThemeToggle({
  sx,
  compact = false,
  orientation = 'horizontal',
  disableDivider = false,
  ...rest
}: ThemeToggleProps) {
  // Read/write once here and pass both down explicitly, rather than letting every
  // ThemeToggleItem read/write useColorScheme() independently - a write from one item's
  // hook call didn't reliably show up in another item's own read within the same render.
  const { mode, setMode } = useColorScheme();

  return (
    <>
      <AccountMenuItem
        onClick={(event: React.MouseEvent<'div'>) => event.stopPropagation()}
        {...rest}
        sx={[{ paddingInline: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <Stack
          direction={orientation === 'vertical' ? 'column' : 'row'}
          sx={{
            gap: 1,
            width: '100%',
            flexWrap: orientation === 'vertical' ? 'nowrap' : 'wrap',
            justifyContent: 'center',
            ...(orientation === 'vertical' ? { alignItems: compact ? 'center' : 'flex-start' } : {}),
          }}
        >
          {themeModes.map((themeMode) => (
            <ThemeToggleItem
              key={themeMode}
              mode={themeMode}
              compact={compact}
              selected={mode === themeMode}
              onClick={() => setMode(themeMode)}
            />
          ))}
        </Stack>
      </AccountMenuItem>
      {!disableDivider && <Divider />}
    </>
  );
}

function upperFirst(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
